import styled from 'styled-components';
import { GlobalToken } from 'antd';

export const MessagesContainer = styled.div<{ theme: { token: GlobalToken } }>`
  flex: 1;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${({ theme: { token } }) => token.paddingXL}px 0; /* Only vertical padding */
  min-height: 0;
  will-change: scroll-position;
  transform: translateZ(0);
  -webkit-overflow-scrolling: touch;
  scroll-behavior: auto;

  @media (max-width: 1200px) {
    padding: ${({ theme: { token } }) => token.paddingLG}px 0;
  }

  @media (max-width: 768px) {
    padding: ${({ theme: { token } }) => token.paddingLG}px 0;
  }

  /* Custom scrollbar styling - thin, discrete, with original color */
  &::-webkit-scrollbar {
    width: 6px !important;
  }

  &::-webkit-scrollbar-track {
    background: transparent !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme: { token } }) => token.colorTextQuaternary} !important;
    border-radius: 3px !important;
    border: none !important;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme: { token } }) => token.colorTextTertiary} !important;
  }

  /* Firefox scrollbar */
  scrollbar-width: thin !important;
  scrollbar-color: ${({ theme: { token } }) => token.colorTextQuaternary} transparent !important;
`;

export const MessagesContent = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { token } }) => token.margin}px;
  max-width: 1200px;
  width: 100%;
  height: 100%;
`;

export const EmptyState = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  text-align: left;
  gap: ${({ theme: { token } }) => token.marginLG}px;
  flex: 1;
  width: 100%;
  height: 100%;
  max-width: 1800px;

  .sparkle-icon {
    font-size: 32px;
    color: ${({ theme: { token } }) => token.colorPrimary};
  }

  .title {
    color: ${({ theme: { token } }) => token.colorText};
    font-size: ${({ theme: { token } }) => token.fontSizeHeading4}px;
    font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
    margin: 0;
    text-align: left;
  }

  .description {
    color: ${({ theme: { token } }) => token.colorTextSecondary};
    font-size: ${({ theme: { token } }) => token.fontSize}px;
    max-width: 600px;
    text-align: left;
    margin: 0;
  }

  .input-wrapper {
    width: 100%;
    max-width: 1800px;
    margin: 0;
  }

  .helper-text {
    color: ${({ theme: { token } }) => token.colorTextSecondary};
    font-size: ${({ theme: { token } }) => token.fontSize}px;
    max-width: 600px;
    margin: 0;
    text-align: left;
  }
`;

export const SuggestedPromptsContainer = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${({ theme: { token } }) => token.marginSM}px;
  margin-top: ${({ theme: { token } }) => token.marginLG}px;
  width: 100%;

  &.no-margin-top {
    margin-top: 0;
  }

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const SuggestedPromptButton = styled.button<{ theme: { token: GlobalToken } }>`
  background: ${({ theme: { token } }) => token.colorFillQuaternary};
  border: 1px solid ${({ theme: { token } }) => token.colorBorderSecondary};
  border-radius: ${({ theme: { token } }) => token.borderRadiusLG}px;
  padding: ${({ theme: { token } }) => token.paddingXS}px ${({ theme: { token } }) => token.paddingSM}px;
  color: ${({ theme: { token } }) => token.colorText};
  font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme: { token } }) => token.marginXXS}px;
  white-space: nowrap;
  height: auto;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  &:hover:not(:disabled) {
    background: ${({ theme: { token } }) => token.colorFillTertiary};
    border-color: ${({ theme: { token } }) => token.colorPrimary};
  }

  &:disabled {
    cursor: not-allowed;
  }

  .badge {
    padding: ${({ theme: { token } }) => token.paddingXXS}px ${({ theme: { token } }) => token.paddingSM}px;
    background: ${({ theme: { token } }) => token.colorError};
    border-radius: ${({ theme: { token } }) => token.borderRadiusSM}px;
    font-size: 9px;
    font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
    color: ${({ theme: { token } }) => token.colorTextLightSolid};
    text-transform: uppercase;
  }
`;

export const MessageWrapper = styled.div<{ role: 'user' | 'assistant'; theme: { token: GlobalToken } }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ role }) => (role === 'user' ? 'flex-end' : 'flex-start')};
  gap: ${({ theme: { token } }) => token.marginXS}px;
  width: 100%;
  padding-left: ${({ role, theme: { token } }) => (role === 'user' ? 0 : token.marginLG)}px;
`;

export const MessageBubble = styled.div<{ theme: { token: GlobalToken } }>`
  max-width: 80%;
  padding: ${({ theme: { token } }) => token.padding}px ${({ theme: { token } }) => token.paddingLG}px;
  border-radius: ${({ theme: { token } }) => token.borderRadiusLG}px;
  background-color: ${({ theme: { token } }) => token.colorBgContainer};
  color: ${({ theme: { token } }) => token.colorTextLightSolid};
  font-size: ${({ theme: { token } }) => token.fontSize}px;
  word-wrap: break-word;
  white-space: pre-wrap;

  p {
    margin: 0 0 ${({ theme: { token } }) => token.marginSM}px 0;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
  }

  ul,
  ol {
    margin: ${({ theme: { token } }) => token.marginXS}px 0;
    padding-left: ${({ theme: { token } }) => token.paddingMD + token.paddingXXS}px;
  }

  li {
    margin: ${({ theme: { token } }) => token.marginXXS}px 0;
  }

  code {
    background: rgba(0, 0, 0, 0.2);
    padding: 2px 6px;
    border-radius: 3px;
    font-family: monospace;
  }
`;

export const MessageTimestamp = styled.span<{ role: 'user' | 'assistant'; theme: { token: GlobalToken } }>`
  font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
  color: ${({ theme: { token } }) => token.colorTextTertiary};
  text-align: ${({ role }) => (role === 'user' ? 'right' : 'center')};
  width: 100%;
`;
